<template>
  <div
    ref="el"
    class="flex min-w-full flex-col bg-white font-secondary text-[14px] leading-[22px] text-gray-800"
    :class="[
      { slideupTransition: animate },
      isViewportHeight ? '' : 'rounded-t-xl',
    ]"
    :style="wrapperStyles"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useDialog } from './useDialog'
import { useDevice } from '@/shared/composables/useDevice'
import type { DialogSlideupHeight } from './types'

const props = withDefaults(
  defineProps<{
    slideupHeight?: DialogSlideupHeight
    disableDismiss?: boolean
  }>(),
  {
    slideupHeight: 'content',
    disableDismiss: false,
  }
)

const { closeAll } = useDialog()
const { viewportHeight } = useDevice()

const el = ref<HTMLElement>()
const touchStartY = ref(0)
const translateY = ref(0)
const animate = ref(false)
const dragging = ref(false)

const isViewportHeight = computed(() => props.slideupHeight === 'viewport')

const wrapperStyles = computed(() => ({
  translate: `0 ${translateY.value}px`,
  ...(isViewportHeight.value && { height: `${viewportHeight.value}px` }),
}))

const toggleAnimation = () => {
  animate.value = true
  setTimeout(() => {
    animate.value = false
  }, 300)
}

const findScrollableParent = (
  target: HTMLElement | null
): HTMLElement | null => {
  while (target && target !== el.value) {
    if (target.scrollHeight > target.clientHeight && target.scrollTop > 0) {
      return target
    }
    target = target.parentElement
  }
  return null
}

const handleTouchStart = (event: TouchEvent) => {
  if (props.disableDismiss) return

  const scrollable = findScrollableParent(event.target as HTMLElement)
  if (scrollable) {
    dragging.value = false
    return
  }
  dragging.value = true
  touchStartY.value = event.touches[0].pageY
}

const handleTouchMove = (event: TouchEvent) => {
  if (!dragging.value) return

  const touch = event.touches[0]
  const delta = touch.pageY - touchStartY.value

  if (delta <= 0) return

  event.preventDefault()
  translateY.value = delta
}

const handleTouchEnd = () => {
  if (!dragging.value) {
    return
  }

  dragging.value = false
  const componentHeight = el.value?.clientHeight || 0

  toggleAnimation()

  if (translateY.value >= componentHeight * 0.5) {
    closeAll()
  }

  translateY.value = 0
}

onMounted(() => {
  el.value?.addEventListener('touchstart', handleTouchStart, { passive: true })
  el.value?.addEventListener('touchmove', handleTouchMove, { passive: false })
  el.value?.addEventListener('touchend', handleTouchEnd, { passive: true })
})

onBeforeUnmount(() => {
  el.value?.removeEventListener('touchstart', handleTouchStart)
  el.value?.removeEventListener('touchmove', handleTouchMove)
  el.value?.removeEventListener('touchend', handleTouchEnd)
})
</script>
